import React from "react";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";

export const useEngagementTheme = () => {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  return {
    dark,
    pageBg: dark ? "#0B1412" : "#F3F5F4",
    cardBg: dark ? "#16211F" : "#FFFFFF",
    inputBg: dark ? "#0E1715" : "#FFFFFF",
    border: dark ? "#2B3A37" : "#DCE2E0",
    borderMuted: dark ? "#223230" : "#EDF0EF",
    hoverBg: dark ? "#1B2927" : "#F1F6F5",
    cellBg: dark ? "#111B19" : "#F8FAF9",
    textPrimary: dark ? "#E8EFEC" : "#15212E",
    textSecondary: dark ? "#C3D0CD" : "#2B3946",
    textMuted: dark ? "#92A6A2" : "#4B5966",
    disabledBorder: dark ? "#2B3A37" : "#C9D2CF",
    disabledText: dark ? "#55665F" : "#A0A8AE",
    accent: "#0F6E6A",
    accentSolidText: "#FFFFFF",
    selectedBg: dark ? "#E8EFEC" : "#15212E",
    selectedText: dark ? "#15212E" : "#FFFFFF",
    tone: {
      crit: dark ? { bg: "#3B211E", color: "#F3A79E" } : { bg: "#FDE8E7", color: "#9B1C14" },
      warn: dark ? { bg: "#3B2E17", color: "#F3C98D" } : { bg: "#FEF0DC", color: "#8A3A05" },
      ok: dark ? { bg: "#133327", color: "#79DCAC" } : { bg: "#E3F4EA", color: "#05603A" },
      info: dark ? { bg: "#182A3D", color: "#8FB7F2" } : { bg: "#E6EEFB", color: "#1849A9" },
      mute: dark ? { bg: "#212E2C", color: "#AEBDB9" } : { bg: "#EDF0EF", color: "#3D4B58" },
    },
    // Lane pills on the Screening Camps board use their own distinct palette
    // (not the generic tone set above) to match the reference prototype.
    lane: {
      req: dark ? { bg: "#182A3D", color: "#8FB7F2" } : { bg: "#E6EEFB", color: "#1849A9" },
      ini: dark ? { bg: "#3B2A1C", color: "#F2B480" } : { bg: "#FDEBDD", color: "#93370D" },
    },
    // Matches the old per-file TXT_TONE maps (text-only tone color, no pill bg).
    txtTone: dark
      ? { crit: "#F3A79E", warn: "#F3C98D", ok: "#79DCAC", info: "#8FB7F2", mute: "#E8EFEC" }
      : { crit: "#9B1C14", warn: "#8A3A05", ok: "#15212E", mute: "#15212E" },
  };
};

export const engagementPageSx = (t) => ({
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  width: "100%",
  minWidth: 0,
  fontFamily: "'Figtree', 'Segoe UI', system-ui, sans-serif",
  color: t.textPrimary,
  bgcolor: t.pageBg,
  margin: "-24px",
  padding: "28px 32px 48px",
  boxSizing: "border-box",
  minHeight: "100%",
});

export const EngagementCard = ({ t, children, sx = {} }) => (
  <Box
    sx={{
      bgcolor: t.cardBg,
      border: `1px solid ${t.border}`,
      borderRadius: "14px",
      padding: "20px",
      display: "flex",
      flexDirection: "column",
      gap: "14px",
      ...sx,
    }}
  >
    {children}
  </Box>
);

export const EngagementPill = ({ t, tone = "mute", children, sx = {} }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      height: "26px",
      px: "10px",
      borderRadius: "13px",
      fontSize: 13,
      fontWeight: 600,
      whiteSpace: "nowrap",
      bgcolor: t.tone[tone]?.bg || t.tone.mute.bg,
      color: t.tone[tone]?.color || t.tone.mute.color,
      ...sx,
    }}
  >
    {children}
  </Box>
);

export const engagementButtonSx = (t, variant = "outline") => ({
  minHeight: 44,
  padding: "0 16px",
  borderRadius: "8px",
  font: "inherit",
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  border: variant === "solid" ? 0 : `1px solid ${t.accent}`,
  bgcolor: variant === "solid" ? t.accent : t.cardBg,
  color: variant === "solid" ? t.accentSolidText : t.accent,
});

export const engagementChipSx = (t, on) => ({
  minHeight: 44,
  padding: "0 16px",
  border: "1px solid",
  borderColor: on ? t.selectedBg : t.disabledBorder,
  borderRadius: "22px",
  font: "inherit",
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  bgcolor: on ? t.selectedBg : t.cardBg,
  color: on ? t.selectedText : t.textPrimary,
});

// Segmented control (e.g. Board / Partner directory / Results view switch).
export const engagementSegSx = (t, on) => ({
  minHeight: 44,
  padding: "0 16px",
  border: 0,
  font: "inherit",
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  bgcolor: on ? t.selectedBg : t.cardBg,
  color: on ? t.selectedText : t.textPrimary,
});

export const engagementSelectSx = (t) => ({
  height: 44,
  minWidth: 160,
  bgcolor: t.inputBg,
  color: t.textPrimary,
  "& .MuiOutlinedInput-notchedOutline": { borderColor: t.border },
});

export const engagementDisabledButtonSx = (t) => ({
  minHeight: 44,
  padding: "0 16px",
  border: `1px solid ${t.disabledBorder}`,
  borderRadius: "8px",
  bgcolor: t.cardBg,
  color: t.disabledText,
  font: "inherit",
  fontSize: 14,
  fontWeight: 600,
  cursor: "not-allowed",
});
